import {
  useState, useEffect, useMemo, useRef,
  Dispatch, SetStateAction, useCallback,
} from 'react';
import { GlobalValuesTypes, SetFormValues } from '@components/common/Forms';
import { dequal } from 'dequal';
import { useSwrJobGenerateQuestions } from '@hooks/useSwrApi/endpoints/job-interview/useSwrJobGenerateQuestions';
import { MirrorData, SetMirrorData } from '@type/job-add';
import { MAX_ENABLED_INTERVIEW_QUESTIONS } from 'src/constants';

/** Props */
interface UseJobInterviewQuestions {
  getFieldValue: (name: string) => GlobalValuesTypes,
  initialInterviewQuestions?: ApiSchema<'InterviewQuestion'>[];
  updateFormElements?: () => void;
  setInterviewQuestionList?: Dispatch<SetStateAction<ApiSchema<'InterviewQuestion'>[] | undefined>>;
  inputName: string;
  shouldFetchQuestions?: boolean,
  reqMirrorData?: Pick<MirrorData, 'title' | 'description'>,
  setMirrorData?: SetMirrorData,
  labelDescriptionClass?: string,
  locale: Locale,
  setFormValues: SetFormValues,
}


/**
 * @description Job Interview Questions hook.
 * - `getFieldValue` - get fields value function from useBjForm
 * - `initialInterviewQuestions` - initial interview questions
 * - `updateFormElements` - update form elements function from useBjForm
 * - `setInterviewQuestionList` - set interview question list function
 * - `inputName` - name of the input
 * - `shouldFetchQuestions` - whether to fetch the domain
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - function to update the mirror form state
 * - `labelDescriptionClass` - additional classes for the label description
 */
export const useJobInterviewQuestions = (props: UseJobInterviewQuestions) => {
  // Destructure props
  const {
    getFieldValue,
    initialInterviewQuestions,
    updateFormElements,
    setInterviewQuestionList,
    inputName,
    shouldFetchQuestions,
    reqMirrorData,
    locale,
  } = props;

  // SWR job domain
  // *************************
  const {
    data: jobGeneratedQuestions,
  } = useSwrJobGenerateQuestions({
    locale,
    payload: {
      title: reqMirrorData?.title ?? '',
      ...(reqMirrorData?.description ? { description: reqMirrorData.description } : {}),
    },
    shouldFetch: shouldFetchQuestions || (!initialInterviewQuestions || initialInterviewQuestions.length === 0),
  });


  const formattedQuestions = useMemo(() => {
    if (!jobGeneratedQuestions?.questions) return [];

    // Filter out duplicates and empty strings from AI response
    const uniqueQuestions = Array.from(new Set(jobGeneratedQuestions.questions.filter((q: string) => q.trim() !== '')));

    return uniqueQuestions.map((q: string) => ({
      question: q,
      enabled: true,
      readOnly: false,
    }));
  }, [jobGeneratedQuestions]);


  // STATES
  // **************************
  // The interview questions
  const prevInitialInterviewQuestions = useRef<ApiSchema<'InterviewQuestion'>[]>(initialInterviewQuestions ?? []);

  /**
   * Helper to limit enabled questions to MAX_ENABLED_INTERVIEW_QUESTIONS
   */
  const limitEnabledQuestions = (questions: ApiSchema<'InterviewQuestion'>[]) => {
    let enabledCount = 0;
    return questions.filter((q) => {
      if (q.enabled) {
        enabledCount += 1;
        return enabledCount <= MAX_ENABLED_INTERVIEW_QUESTIONS;
      }
      return true;
    });
  };

  const [interviewQuestions, setInterviewQuestions] = useState<ApiSchema<'InterviewQuestion'>[]>(
    limitEnabledQuestions(initialInterviewQuestions ?? formattedQuestions ?? []),
  );
  // Reset interview questions if initialInterviewQuestions change
  useEffect(() => {
    if (!dequal(prevInitialInterviewQuestions.current, initialInterviewQuestions)) {
      prevInitialInterviewQuestions.current = initialInterviewQuestions ?? [];
      setInterviewQuestions(limitEnabledQuestions(initialInterviewQuestions ?? []));
    }
  }, [initialInterviewQuestions]);

  // Interview record options
  const getInterviewRecordOptions = useCallback(() => interviewQuestions.map((question, index) => ({
    id: `${inputName}_${index}`,
    defaultValue: question.question,
    valueAsArray: true,
    removeFromSubmit: true,
  })), [interviewQuestions, inputName]);
  const interviewRecordOptions = getInterviewRecordOptions();


  // FUNCTIONS
  // **************************
  // Add a question.
  const addQuestion = () => {
    setInterviewQuestions((prev) => [...prev, { question: '', enabled: true, readOnly: false }]);
  };

  // Delete a question.
  const deleteQuestion = (questionIndex: number) => {
    setInterviewQuestions((prev) => {
      const questions = [...prev];
      questions.splice(questionIndex, 1);
      return questions;
    });
  };

  // Save questions
  const saveQuestions = () => {
    const values = getFieldValue(inputName) as string[];
    // Map the interviewQuestions state and update the text
    setInterviewQuestions((prev) => {
      const newQuestions = prev.map((question, index) => {
        const text = values?.[index] ?? '';
        return { ...question, question: text };
      });

      if (!dequal(prev, newQuestions)) {
        return newQuestions;
      }
      return prev;
    });
  };


  // SET MAIN STATE THAT WILL BE PASSED TO FORM
  // **************************
  useEffect(() => {
    if (setInterviewQuestionList && interviewQuestions.length > 0) {
      updateFormElements?.();
      // filter out empty questions
      const filteredQuestions = interviewQuestions.filter((question) => question.question !== '');
      setInterviewQuestionList((prev) => {
        if (filteredQuestions.length === 0) return undefined;
        // Don't update if the questions are the same
        if (!dequal(prev, filteredQuestions)) return filteredQuestions;
        return prev;
      });
    }
  }, [interviewQuestions, setInterviewQuestionList, updateFormElements]);

  // Update interviewQuestions whenever formattedQuestions changes (e.g., after fetch)
  useEffect(() => {
    if (formattedQuestions.length > 0) {
      setInterviewQuestions((prev) => {
        // Only overwrite if the current list is empty or only has one empty question
        const isEmpty = prev.length === 0 || (prev.length === 1 && prev[0]?.question?.trim() === '');
        if (isEmpty) {
          return limitEnabledQuestions(formattedQuestions);
        }
        return prev;
      });
    }
  }, [formattedQuestions]);


  // Return
  // **************************
  return {
    interviewRecordOptions,
    interviewQuestions,
    setInterviewQuestions,
    addQuestion,
    deleteQuestion,
    saveQuestions,
  };
};
